import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createStripeCheckout } from '../../../lib/stripeCheckout';

interface TecnicaPNL {
  id: string;
  icon: string;
  emoji: string;
  titulo: string;
  subtitulo: string;
  preco: number;
  cor: string;
  duracao: string;
  nivel: string;
  beneficiosResumidos: string[];
  worksheetLink?: string;
  freeVersionLink?: string;
}

const tecnicasPNL: TecnicaPNL[] = [
  {
    id: 'ancoragem-emocional',
    icon: 'ri-anchor-line',
    emoji: '⚓',
    titulo: 'Ancoragem Emocional',
    subtitulo: 'Gatilhos instantâneos de estados positivos',
    preco: 18,
    cor: '#C9A84C',
    duracao: '55 min',
    nivel: 'Intermediário',
    beneficiosResumidos: [
      'Acesso instantâneo a estados emocionais positivos',
      'Controle emocional em situações desafiadoras',
      'Múltiplas âncoras para diferentes contextos'
    ],
    freeVersionLink: '/exercicio-gratis/ancoragem-emocional'
  },
  {
    id: 'encantations-tony-robbins',
    icon: 'ri-fire-line',
    emoji: '🔥',
    titulo: 'Encantations',
    subtitulo: 'Técnica Tony Robbins — Palavras + Movimento',
    preco: 18,
    cor: '#E67E22',
    duracao: '65 min',
    nivel: 'Intermediário',
    beneficiosResumidos: [
      'Reprogramação profunda de crenças limitantes',
      'Aumento exponencial de energia e motivação',
      'Resultados mensuráveis em 21 dias'
    ],
    worksheetLink: '/worksheet-encantations',
    freeVersionLink: '/exercicio-gratis/encantations-tony-robbins'
  },
  {
    id: 'tecnica-swish-pnl',
    icon: 'ri-swap-line',
    emoji: '⚡',
    titulo: 'Técnica Swish',
    subtitulo: 'Reprogramação rápida de respostas automáticas',
    preco: 18,
    cor: '#27AE60',
    duracao: '60 min',
    nivel: 'Intermediário',
    beneficiosResumidos: [
      'Substituição rápida de padrões indesejados',
      'Eliminação de procrastinação e autossabotagem',
      'Instalação automática de novos padrões'
    ],
    worksheetLink: '/worksheet-swish',
    freeVersionLink: '/exercicio-gratis/tecnica-swish-pnl'
  },
  {
    id: 'ressignificacao-reframing-pnl',
    icon: 'ri-shape-line',
    emoji: '🔄',
    titulo: 'Ressignificação (Reframing)',
    subtitulo: 'Transforme o significado de experiências',
    preco: 18,
    cor: '#9B59B6',
    duracao: '55 min',
    nivel: 'Intermediário',
    beneficiosResumidos: [
      'Transformar experiências negativas em aprendizados',
      'Superar crenças limitantes rapidamente',
      'Desenvolver resiliência emocional profunda'
    ],
    worksheetLink: '/worksheet-ressignificacao',
    freeVersionLink: '/exercicio-gratis/ressignificacao-reframing-pnl'
  },
  {
    id: 'linha-tempo-pnl',
    icon: 'ri-time-line',
    emoji: '🕰️',
    titulo: 'Linha do Tempo (Timeline)',
    subtitulo: 'Reorganize sua história e liberte-se do passado',
    preco: 18,
    cor: '#2980B9',
    duracao: '75 min',
    nivel: 'Avançado',
    beneficiosResumidos: [
      'Liberar emoções negativas do passado',
      'Eliminar decisões limitantes antigas',
      'Criar um futuro motivador e alinhado'
    ],
    worksheetLink: '/worksheet-linha-tempo',
    freeVersionLink: '/exercicio-gratis/linha-tempo-pnl'
  },
  {
    id: 'submodalidades-pnl',
    icon: 'ri-equalizer-line',
    emoji: '🎛️',
    titulo: 'Submodalidades',
    subtitulo: 'Domine os controles internos da sua mente',
    preco: 18,
    cor: '#E74C3C',
    duracao: '50 min',
    nivel: 'Intermediário',
    beneficiosResumidos: [
      'Controlar a intensidade de qualquer emoção',
      'Diminuir impacto de memórias dolorosas',
      'Técnica base para todas as ferramentas de PNL'
    ],
    worksheetLink: '/worksheet-submodalidades',
    freeVersionLink: '/exercicio-gratis/submodalidades-pnl'
  }
];

const PRECO_PACOTE = 47;
const PRECO_INDIVIDUAL_TOTAL = 108;
const ECONOMIA = PRECO_INDIVIDUAL_TOTAL - PRECO_PACOTE;

export default function ExerciciosPNL() {
  const [loadingCheckout, setLoadingCheckout] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const handleCheckoutIndividual = async (tecnicaId: string) => {
    setLoadingCheckout(tecnicaId);
    try {
      const currentUrl = window.location.origin;
      const checkoutUrl = await createStripeCheckout({
        priceId: `price_pnl_${tecnicaId}_18`,
        mode: 'payment',
        successUrl: `${currentUrl}/planos/sucesso?plan=pnl-${tecnicaId}`,
        cancelUrl: `${currentUrl}/planos?canceled=true`,
        metadata: {
          plan: `pnl-${tecnicaId}`,
          type: 'pnl-individual'
        }
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
    } finally {
      setLoadingCheckout(null);
    }
  };

  const handleCheckoutPacote = async () => {
    setLoadingCheckout('pacote');
    try {
      const currentUrl = window.location.origin;
      const checkoutUrl = await createStripeCheckout({
        priceId: 'price_pacote_pnl_47',
        mode: 'payment',
        successUrl: `${currentUrl}/planos/sucesso?plan=pacote-pnl`,
        cancelUrl: `${currentUrl}/planos?canceled=true`,
        metadata: {
          plan: 'pacote-pnl-completo',
          type: 'pnl-bundle'
        }
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
    } finally {
      setLoadingCheckout(null);
    }
  };

  return (
    <section id="exercicios-pnl" className="py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #111111 50%, #0A0A0A 100%)' }}>
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent"></div>
      <div className="absolute top-20 right-10 w-96 h-96 bg-gold-500/3 rounded-full blur-[150px]"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-amber-500/3 rounded-full blur-[120px]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-gold-500/15 to-amber-500/15 rounded-full border border-gold-500/20 mb-8">
            <i className="ri-brain-line text-lg text-gold-400"></i>
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400 font-semibold">
              Programação Neurolinguística
            </span>
            <i className="ri-brain-line text-lg text-gold-400"></i>
          </div>

          <h2 className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white mb-6 leading-tight">
            Exercícios de <span className="text-gold-400 italic">PNL</span>
          </h2>

          <div className="flex justify-center mb-6">
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-gold-500 to-transparent"></div>
          </div>

          <p className="font-['Montserrat'] text-base text-white/50 max-w-3xl mx-auto font-light tracking-wide leading-relaxed">
            6 técnicas poderosas de Programação Neurolinguística para reprogramar sua mente, 
            eliminar padrões limitantes e instalar novos comportamentos de excelência.
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-10">
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl font-bold text-gold-400">6</p>
              <p className="font-['Montserrat'] text-xs text-white/40 uppercase tracking-wider mt-1">Técnicas</p>
            </div>
            <div className="w-px h-10 bg-gold-500/20"></div>
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl font-bold text-gold-400">360</p>
              <p className="font-['Montserrat'] text-xs text-white/40 uppercase tracking-wider mt-1">Minutos</p>
            </div>
            <div className="w-px h-10 bg-gold-500/20"></div>
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl font-bold text-gold-400">R$ 18</p>
              <p className="font-['Montserrat'] text-xs text-white/40 uppercase tracking-wider mt-1">Cada</p>
            </div>
          </div>
        </div>

        {/* ===== PACOTE PROMOCIONAL DESTAQUE ===== */}
        <div className="mb-16 relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-gold-500/20 via-amber-400/20 to-gold-500/20 rounded-3xl blur-lg"></div>
          <div className="relative bg-gradient-to-br from-luxury-charcoal via-luxury-dark to-luxury-charcoal rounded-3xl border-2 border-gold-500/30 overflow-hidden">
            {/* Ribbon */}
            <div className="absolute top-0 right-0 overflow-hidden w-40 h-40">
              <div className="absolute top-6 -right-10 w-56 bg-gradient-to-r from-amber-500 to-gold-500 text-luxury-black font-['Montserrat'] font-black text-xs py-2 text-center transform rotate-45 shadow-lg uppercase tracking-wider">
                Economize 56%
              </div>
            </div>

            <div className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-10 items-center">
                {/* Left - Info */}
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 rounded-full mb-6">
                    <i className="ri-gift-2-fill text-gold-400"></i>
                    <span className="font-['Montserrat'] text-xs font-bold text-gold-400 uppercase tracking-wider">
                      Pacote Promocional
                    </span>
                  </div>

                  <h3 className="font-['Playfair_Display'] text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                    🧠 Todas as 6 Técnicas de <span className="text-gold-400">PNL</span>
                  </h3>

                  <p className="font-['Montserrat'] text-sm text-white/50 mb-8 leading-relaxed font-light">
                    Domine as técnicas mais poderosas da Programação Neurolinguística em um único pacote com desconto exclusivo.
                  </p>

                  {/* Included techniques */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {tecnicasPNL.map((t) => (
                      <div key={t.id} className="flex items-center gap-3 px-4 py-3 bg-white/[0.03] rounded-xl border border-gold-500/10">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${t.cor}20` }}>
                          <i className={`${t.icon} text-sm`} style={{ color: t.cor }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Montserrat'] text-xs font-semibold text-white truncate">{t.emoji} {t.titulo}</p>
                          <p className="font-['Montserrat'] text-[10px] text-white/30">{t.duracao} • {t.nivel}</p>
                        </div>
                        <i className="ri-check-line text-sm text-emerald-400 flex-shrink-0"></i>
                      </div>
                    ))}
                  </div>

                  {/* Extra benefits */}
                  <div className="space-y-2">
                    {[
                      'Acesso vitalício a todas as 6 técnicas',
                      'Worksheets interativos inclusos',
                      'Atualizações futuras gratuitas'
                    ].map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-[10px] text-emerald-400"></i>
                        </div>
                        <span className="font-['Montserrat'] text-xs text-white/50">{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right - Pricing & CTA */}
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white/[0.03] rounded-2xl border border-gold-500/15 p-8 w-full max-w-sm">
                    {/* Price comparison */}
                    <p className="font-['Montserrat'] text-xs text-white/40 uppercase tracking-wider mb-2">Se comprar separado</p>
                    <p className="font-['Playfair_Display'] text-2xl text-white/30 line-through mb-4">
                      R$ {PRECO_INDIVIDUAL_TOTAL}
                    </p>

                    <div className="w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent mb-6"></div>

                    <p className="font-['Montserrat'] text-xs text-gold-400 uppercase tracking-wider font-semibold mb-2">
                      Preço do Pacote
                    </p>
                    <div className="flex items-baseline justify-center gap-1 mb-2">
                      <span className="font-['Montserrat'] text-lg text-white/50">R$</span>
                      <span className="font-['Playfair_Display'] text-7xl font-bold text-gold-400 leading-none">47</span>
                    </div>
                    <p className="font-['Montserrat'] text-xs text-white/30 mb-2">pagamento único • acesso vitalício</p>

                    {/* Savings badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-8">
                      <i className="ri-arrow-down-line text-emerald-400 text-sm"></i>
                      <span className="font-['Montserrat'] text-xs font-bold text-emerald-400">
                        Economia de R$ {ECONOMIA}
                      </span>
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={handleCheckoutPacote}
                      disabled={loadingCheckout === 'pacote'}
                      className="w-full py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-luxury-black rounded-xl font-['Montserrat'] font-black text-sm hover:shadow-[0_0_40px_rgba(201,168,76,0.3)] hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingCheckout === 'pacote' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Processando...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-shopping-bag-line"></i>
                          Quero o Pacote Completo
                        </span>
                      )}
                    </button>

                    <p className="font-['Montserrat'] text-[10px] text-white/20 mt-4 flex items-center justify-center gap-1">
                      <i className="ri-shield-check-line"></i>
                      Garantia de 7 dias • Pagamento seguro via Stripe
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== TÉCNICAS INDIVIDUAIS ===== */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white">
                Ou adquira <span className="text-gold-400 italic">individualmente</span>
              </h3>
              <p className="font-['Montserrat'] text-xs text-white/40 mt-1 tracking-wide">
                Cada técnica por R$ 18,00 com acesso único e vitalício
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tecnicasPNL.map((tecnica) => (
              <div
                key={tecnica.id}
                onMouseEnter={() => setHoveredCard(tecnica.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`group relative bg-white/[0.03] rounded-2xl border transition-all duration-400 p-6 ${
                  hoveredCard === tecnica.id
                    ? 'border-gold-500/40 -translate-y-2 shadow-[0_0_40px_rgba(201,168,76,0.1)]'
                    : 'border-gold-500/10 hover:border-gold-500/20'
                }`}
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${tecnica.cor}15` }}
                  >
                    <i className={`${tecnica.icon} text-xl`} style={{ color: tecnica.cor }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-['Playfair_Display'] text-lg font-bold text-white group-hover:text-gold-400 transition-colors">
                      {tecnica.emoji} {tecnica.titulo}
                    </h4>
                    <p className="font-['Montserrat'] text-[11px] text-white/35 mt-0.5 leading-snug">
                      {tecnica.subtitulo}
                    </p>
                  </div>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2.5 py-1 bg-white/[0.05] rounded-full font-['Montserrat'] text-[10px] text-white/40 tracking-wider">
                    <i className="ri-time-line mr-1"></i>{tecnica.duracao}
                  </span>
                  <span className="px-2.5 py-1 bg-white/[0.05] rounded-full font-['Montserrat'] text-[10px] text-white/40 tracking-wider">
                    <i className="ri-bar-chart-line mr-1"></i>{tecnica.nivel}
                  </span>
                </div>

                {/* Benefits */}
                <div className="space-y-2 mb-5">
                  {tecnica.beneficiosResumidos.map((b, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full bg-gold-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[9px] text-gold-400"></i>
                      </div>
                      <span className="font-['Montserrat'] text-xs text-white/45 leading-relaxed">{b}</span>
                    </div>
                  ))}
                </div>

                {/* Price & Actions */}
                <div className="pt-4 border-t border-gold-500/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-baseline gap-1">
                      <span className="font-['Playfair_Display'] text-2xl font-bold text-gold-400">R$ {tecnica.preco}</span>
                      <span className="font-['Montserrat'] text-[10px] text-white/30">único</span>
                    </div>
                    {tecnica.worksheetLink && (
                      <Link
                        to={tecnica.worksheetLink}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.05] rounded-lg border border-gold-500/10 hover:border-gold-500/30 transition-all cursor-pointer"
                      >
                        <i className="ri-file-text-line text-xs text-gold-400"></i>
                        <span className="font-['Montserrat'] text-[10px] text-white/50">Worksheet</span>
                      </Link>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {tecnica.freeVersionLink && (
                      <Link
                        to={tecnica.freeVersionLink}
                        className="col-span-3 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border border-emerald-500/30 text-emerald-400 font-['Montserrat'] font-semibold text-xs hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase text-center flex items-center justify-center gap-1.5"
                      >
                        <i className="ri-gift-line"></i>
                        Experimentar Grátis
                      </Link>
                    )}
                    <Link
                      to={`/exercicio/${tecnica.id}`}
                      className="col-span-1 py-3 rounded-lg border border-gold-500/20 text-gold-400 font-['Montserrat'] font-semibold text-xs hover:bg-gold-500/10 hover:border-gold-500/40 transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase text-center flex items-center justify-center"
                    >
                      <i className="ri-eye-line"></i>
                    </Link>
                    <button
                      onClick={() => handleCheckoutIndividual(tecnica.id)}
                      disabled={loadingCheckout === tecnica.id}
                      className="col-span-2 py-3 rounded-lg bg-gradient-to-r from-gold-500 to-gold-700 text-luxury-black font-['Montserrat'] font-bold text-xs hover:shadow-[0_0_20px_rgba(201,168,76,0.2)] transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase disabled:opacity-50"
                    >
                      {loadingCheckout === tecnica.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        'Comprar'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom comparison note */}
        <div className="mt-12 bg-gradient-to-r from-gold-500/5 via-gold-500/10 to-gold-500/5 rounded-2xl p-8 border border-gold-500/15 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <i className="ri-lightbulb-flash-line text-2xl text-gold-400"></i>
            <h4 className="font-['Playfair_Display'] text-xl font-bold text-white">
              Por que escolher o Pacote?
            </h4>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-money-dollar-circle-line text-xl text-emerald-400"></i>
              </div>
              <p className="font-['Montserrat'] text-sm font-semibold text-white mb-1">Economia de 56%</p>
              <p className="font-['Montserrat'] text-xs text-white/40">De R$ 108 por apenas R$ 47</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-puzzle-line text-xl text-gold-400"></i>
              </div>
              <p className="font-['Montserrat'] text-sm font-semibold text-white mb-1">Técnicas Complementares</p>
              <p className="font-['Montserrat'] text-xs text-white/40">Cada técnica potencializa as outras</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-infinity-line text-xl text-amber-400"></i>
              </div>
              <p className="font-['Montserrat'] text-sm font-semibold text-white mb-1">Acesso Vitalício</p>
              <p className="font-['Montserrat'] text-xs text-white/40">Pratique quantas vezes quiser, para sempre</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}